import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Backdrop,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Divider,
  Tooltip
} from "@mui/material";
import successIcon from "../../assets/senddialog/success.svg";
import failIcon from "../../assets/senddialog/fail.svg";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';

import { useForm } from "react-hook-form";
import { isEmail } from "class-validator";
import { getColors } from "../../themes/theme";

import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

const SharePage = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      senderInfo: "",
    },
  });

  const { darkMode } = useContext(ThemeContext);
  const [videos, setVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [videoLanguageTab, setVideoLanguageTab] = useState("en");
  const [loading, setLoading] = useState(true); // page-level loading
  const [error, setError] = useState("");
  const { token, user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const baseURL = window.location.origin;

  // --- AI Modal State ---
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [loadingState, setLoadingState] = useState("idle");
  const [animationText, setAnimationText] = useState("Thinking up the perfect message…");
  const [schedulePlan, setSchedulePlan] = useState([]);
  const [scheduleRecipient, setScheduleRecipient] = useState("");
  const [scheduleTz, setScheduleTz] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");

  function isoToLocalInput(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mi = pad(d.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }

  // Given a local input string from <input type="datetime-local">,
  // return the *instant* (UTC ISO) it represents.
  function localInputToUTCISO(localStr) {
    if (!localStr) return null;
    const d = new Date(localStr); // interpreted in user's local tz as an instant
    return d.toISOString();
  }

  // Format a Date (instant) into "YYYY-MM-DDTHH:MM" as seen in a given IANA tz.
  // (This is DST-safe.)
  function formatInstantInTz(date, timeZone) {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = fmt.formatToParts(date).reduce((a, p) => {
      if (p.type !== "literal") a[p.type] = p.value;
      return a;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  }

  // Convert a *tz-local wall time* "YYYY-MM-DDTHH:MM" (for a given tz) to UTC ISO.
  // Useful if the backend wants an absolute instant corresponding to scheduleTz wall time.
  function zonedLocalToUTCISO(localInTz, timeZone) {
    const [y, m, d, hh, mi] = localInTz.split(/[-T:]/).map(Number);
    // Start with the naive UTC guess for those components
    const utcGuess = Date.UTC(y, m - 1, d, hh, mi);
    const guess = new Date(utcGuess);

    // What does that guess look like *in* timeZone?
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const gotParts = fmt.formatToParts(guess).reduce((a, p) => {
      if (p.type !== "literal") a[p.type] = Number(p.value);
      return a;
    }, {});

    const gotUTC = Date.UTC(
      gotParts.year,
      gotParts.month - 1,
      gotParts.day,
      gotParts.hour,
      gotParts.minute
    );

    // Adjust so that formatting in timeZone matches the requested wall time
    const diff = utcGuess - gotUTC;
    return new Date(guess.getTime() + diff).toISOString();
  }


  useEffect(() => {
    (async () => {
      try {
        if (!user || !token) return;
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/video/share`,
          { userId: user.userid },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVideos(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch videos. Please try again later.");
        setLoading(false);
      }
    })();
  }, [token, user]);

  // fun little rotating hints during AI loading (optional)
  useEffect(() => {
    if (loadingState !== "loading") return;
    const hints = [
      "Analyzing Practice Context…",
      "Selecting best tone and length…",
      "Polishing phrasing…",
      "Almost there…",
    ];
    let i = 0;
    setAnimationText(hints[i]);
    const t = setInterval(() => {
      i = (i + 1) % hints.length;
      setAnimationText(hints[i]);
    }, 1700);
    return () => clearInterval(t);
  }, [loadingState]);

  const phoneRegex = /^[0-9]{10}$/;

  const extractSchedule = (respData) => {
    const schedule = respData?.schedule ?? respData?.data?.schedule ?? null;
    if (!schedule) return null;
    return {
      plan: Array.isArray(schedule.plan) ? schedule.plan : [],
      recipientAddress: schedule.recipientAddress ?? "",
      timezone: schedule.timezone ?? "UTC",
      note: schedule.note ?? "",
    };
  };

  const onSubmit = async (data, useAI = false) => {
    const { senderInfo } = data;

    if (!phoneRegex.test(senderInfo) && !isEmail(senderInfo)) return;

    if (selectedVideos.length === 0) {
      enqueueSnackbar("Please select at least one video to share.", { variant: "error" });
      return;
    }

    const videosPayload = selectedVideos.map((v) => ({
      title: v.displayname || v.title || "Untitled Video",
      url: `${baseURL}/shared-videos/${v.encryptlink}`,
    }));

    // For non-AI: preserve existing UX (page overlay spinner + snackbar)
    if (!useAI) {
      setLoading(true);
    } else {
      // For AI: show modal with custom loading animation
      setAiModalOpen(true);
      setLoadingState("loading");
    }

    try {
      const endpoint = isEmail(senderInfo)
        ? `${process.env.REACT_APP_API_URL}/send-email/video`
        : `${process.env.REACT_APP_API_URL}/send-text/video`;

      const payload = {
        practiceId: `${user?.practiceId}`,
        recipientAddress: senderInfo,
        videos: videosPayload,
        subject: `${user?.practiceName} recommends these educational videos.`,
        type: "video",
        useAI,
      };

      const resp = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!useAI) {
        enqueueSnackbar(
          `Sent ${videosPayload.length} link${videosPayload.length > 1 ? "s" : ""} successfully.`,
          { variant: "success" }
        );
      } else {
        const sched = extractSchedule(resp?.data);
        if (!sched) {
          setLoadingState("fail");
        } else {
          setScheduleRecipient(sched.recipientAddress);
          setScheduleTz(sched.timezone);
          setScheduleNote(sched.note);
          console.log(scheduleNote);
          // Initialize editable plan rows
          setSchedulePlan(
            (sched.plan || []).map((item) => ({
              ...item,
              // store what's shown/edited as *local input string* for the user
              send_at: item.send_at ? isoToLocalInput(item.send_at) : "",
            }))
          );
          setLoadingState("success");
        }
      }
    } catch (err) {
      if (!useAI) {
        enqueueSnackbar("Error sending messages!", { variant: "error" });
      } else {
        setLoadingState("fail");
      }
    } finally {
      if (!useAI) {
        setLoading(false);
      }
    }
  };

  const handleSaveSchedule = async () => {
    if (!schedulePlan.length) {
      enqueueSnackbar("Nothing to save.", { variant: "warning" });
      return;
    }
    const hasAny = schedulePlan.some((i) => i.send_at && i.send_at.length > 0);
    if (!hasAny) {
      enqueueSnackbar("Please pick at least one date/time.", { variant: "warning" });
      return;
    }

    try {
      // User chose times in *their* local tz -> convert to scheduleTz
      const plan = schedulePlan.map((i) => {
        if (!i.send_at) {
          return { video_title: i.video_title, video_url: i.video_url, send_at: null };
        }
        console.log("sendAt: ", i.send_at);
        // 1) Interpret the user's input as an instant
        const instantUTC = localInputToUTCISO(i.send_at);
        console.log("instantUTC: ", instantUTC);
        // 2) Express that instant in the schedule's time zone (wall time)
        const scheduleWall = formatInstantInTz(new Date(instantUTC), scheduleTz);
        
        console.log("scheduleWall: ", scheduleWall);
        // ---- PICK ONE TO SEND ----
        // Option A: absolute instant for the schedule's wall time
        const sendAtUTCFromSchedule = zonedLocalToUTCISO(scheduleWall, scheduleTz);
        
        console.log("sendAtUTCFromSchedule: ", sendAtUTCFromSchedule);
        // Option B: just the wall time string, let backend combine with timezone
        // const sendAtWall = scheduleWall;

        return {
          video_title: i.video_title,
          video_url: i.video_url,
          // A) unambiguous instant (preferred)
          send_at: sendAtUTCFromSchedule,

          // If you choose B instead, swap to:
          // send_at: sendAtWall,
        };
      });

      await axios.post(
        `${process.env.REACT_APP_API_URL}/send-ai/save-schedule`,
        {
          schedule: {
            practiceId: user.practiceId,
            recipientAddress: scheduleRecipient,
            channel: isEmail(scheduleRecipient) ? "email" : "sms",
            timezone: scheduleTz,
            plan,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      enqueueSnackbar("Schedule saved.", { variant: "success" });
      setAiModalOpen(false);
      setLoadingState("idle");
    } catch (e) {
      enqueueSnackbar("Failed to save schedule.", { variant: "error" });
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed"; // Avoid scrolling to bottom
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      enqueueSnackbar(successful ? "Link copied!" : "Copy failed.", {
        variant: successful ? "success" : "error",
      });
    } catch (err) {
      enqueueSnackbar("Copy failed due to an error.", { variant: "error" });
    }

    document.body.removeChild(textArea);
  };

  const copyToClipboard = (url) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(
        () => {
          enqueueSnackbar("Link copied to clipboard!", { variant: "success" });
        },
        () => {
          fallbackCopyToClipboard(url);
        }
      );
    } else {
      fallbackCopyToClipboard(url);
    }
  };

  const filteredVideos = videos.filter((video) => video.language === videoLanguageTab);

  const toggleVideoSelect = (video) => {
    setSelectedVideos((prev) => {
      const exists = prev.some((v) => v.id === video.id);
      const next = exists ? prev.filter((v) => v.id !== video.id) : [...prev, video];
      return next;
    });

    const shareUrl = `${baseURL}/shared-videos/${video.encryptlink}`;
    copyToClipboard(shareUrl);
  };


  const isSelected = (id) => selectedVideos.some((v) => v.id === id);

  return (
    <>
      {/* Existing page-level overlay for initial load or non-AI send */}
      <Backdrop sx={{ zIndex: 1400 }} open={loading}>
        <CircularProgress size={80} />
      </Backdrop>

      {/* AI modal */}
      <Dialog
        open={aiModalOpen}
        onClose={() => (loadingState === "loading" ? null : (setAiModalOpen(false), setLoadingState("idle")))}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden" , background: getColors.listItemHoverBg},
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Poppins",
            fontWeight: 600,
            color: 'white',
            pr: 6,
          }}
        >
          {loadingState === "loading" && "Scheduling with AI"}
          {loadingState === "success" && "Automation Schedule Ready"}
          {loadingState === "fail" && "Something went wrong"}
          <IconButton
            aria-label="close"
            onClick={() => (loadingState === "loading" ? null : (setAiModalOpen(false), setLoadingState("idle")))}
            sx={{ position: "absolute", right: 8, top: 8 }}
            disabled={loadingState === "loading"}
          >
            <CloseIcon sx={{color: "white"}}/>
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          {loadingState === "loading" && (
            <Box display="flex" justifyContent="center" sx={{ mt: 0 }}>
              <Box>
                <video
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  loop
                  muted
                  style={{ width: "300px", height: "300px" }}
                >
                  <source src="/assets/loading.webm" type="video/webm" />
                  Your browser does not support the video tag.
                </video>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "16px", sm: "20px" },
                    color: getColors.buttonText,
                    fontWeight: 400,
                    textAlign: "center",
                    mb: "26px",
                  }}
                >
                  {animationText}
                </Typography>
              </Box>
            </Box>
          )}

          {loadingState === "success" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              sx={{ mt: 0, color: "#fff" }}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <img
                  src={successIcon}
                  alt="Success"
                  style={{ width: "100px", height: "100px" }}
                />
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "16px", sm: "20px" },
                    fontWeight: 500,
                    textAlign: "center",
                    mb: 1
                  }}
                >
                  Suggested Plan Ready
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "12px", sm: "13px" },
                    opacity: 0.9,
                    textAlign: "center",
                    mb: 2
                  }}
                >
                  Recipient: {scheduleRecipient}
                </Typography>
              </Box>
              
              {scheduleNote && (
                <Box
                  role="note"
                  aria-label="Why this schedule works"
                  sx={{
                    position: "relative",
                    display: "flex",
                    gap: 1.25,
                    alignItems: "flex-start",
                    p: 2,
                    mb: 2.5,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                    overflow: "hidden",
                  }}
                >
                  {/* Left accent bar */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: "rgba(255,255,255,0.6)",
                    }}
                  />

                  {/* Icon */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(255,255,255,0.12)",
                      mt: 0.25
                    }}
                  >
                    {/* If you already use MUI icons, swap for <LightbulbOutlined /> */}
                    <span style={{ fontSize: 16, lineHeight: 1 }}>💡</span>
                  </Box>

                  {/* Text */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: 13,
                        letterSpacing: 0.2,
                        textTransform: "uppercase",
                        opacity: 0.9,
                        mb: 0.5,
                      }}
                    >
                      Why this schedule works
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: { xs: 13, sm: 14 },
                        color: "#fff",
                        opacity: 0.95,
                        lineHeight: 1.55,
                      }}
                    >
                      {scheduleNote}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Compact editable table/list */}
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.08)",
                  borderRadius: 2,
                  p: 2,
                  maxHeight: 320,
                  overflowY: "auto"
                }}
              >
                {schedulePlan.length === 0 ? (
                  <Typography sx={{ fontFamily: "Poppins", fontSize: 14, opacity: 0.9 }}>
                    No items in plan.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {schedulePlan.length === 0 ? (
                      <Typography sx={{ fontFamily: "Poppins", fontSize: 14, opacity: 0.9, color: "#fff" }}>
                        No items in plan.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {schedulePlan.map((row, idx) => (
                          <Box
                            key={`${row.video_title}-${idx}`}
                            sx={{
                              display: "grid",
                              // On mobile stack; from sm+ give the picker a flexible but *minimum* width
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "minmax(0,1fr) clamp(260px, 40%, 420px)"
                              },
                              columnGap: 1.5,
                              rowGap: 0.5,
                              alignItems: "center"
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Poppins",
                                fontSize: 14,
                                fontWeight: 500,
                                pr: { sm: 2 },
                                color: "#fff",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                wordBreak: "break-word"
                              }}
                            >
                              {row.video_title}
                            </Typography>

                            {/* Datetime: larger, flexible column; date+time together; past times disabled */}
                            <TextField
                              type="datetime-local"
                              value={row.send_at || ""} // <-- use the user-local string directly
                              onChange={(e) => {
                                const v = e.target.value; // "YYYY-MM-DDTHH:MM" in user's local tz
                                setSchedulePlan((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, send_at: v } : r))
                                );
                              }}
                              variant="filled"
                              fullWidth
                              inputProps={{
                                min: isoToLocalInput(),
                                step: 60,
                              }}
                              sx={{
                                justifySelf: { sm: "end" },
                                width: "100%",
                                "& .MuiInputBase-input": { color: "#fff", fontFamily: "Poppins" },
                                "& .MuiFormLabel-root": { color: "#fff" },
                                "& .MuiFilledInput-root": {
                                  backgroundColor: "rgba(255,255,255,0.06)",
                                  "&:before": { borderBottomColor: "rgba(255,255,255,0.4)" },
                                  "&:hover:before": { borderBottomColor: "#fff" },
                                  "&.Mui-focused:after": { borderBottomColor: "#fff" },
                                },
                                "& input::-webkit-calendar-picker-indicator": { filter: "invert(1)" },
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                )}
              </Box>

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  onClick={() => {
                    setAiModalOpen(false);
                    setLoadingState("idle");
                  }}
                  variant="text"
                  sx={{ color: "#fff" }} // white text
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSchedule}
                  variant="contained"
                  sx={{
                    color: "#fff", // ensure text stays white
                    boxShadow: "none"
                  }}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          )}

          {loadingState === "fail" && (
            <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <img
                  src={failIcon}
                  alt="Fail"
                  style={{ width: "150px", height: "150px" }}
                />
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "16px", sm: "20px" },
                    color: getColors.buttonText,
                    fontWeight: 400,
                    my: "26px",
                    textAlign: "center",
                  }}
                >
                  Generation Failed!
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    setLoadingState("idle");
                    setAiModalOpen(false);
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Page */}
      <Box
        px={{ xs: "10px", sm: 3, md: "30px", lg: 4, xl: 8 }}
        py={{ xs: 4, sm: 3, md: "30px", lg: 4, xl: 8 }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: "24px", sm: "28px" },
            fontWeight: 500,
            color: darkMode ? getColors.drawerTextDark : getColors.thumbnailTextLight,
            mb: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          Send Patient Education Video
        </Typography>

        <form onSubmit={handleSubmit((data) => onSubmit(data, false))}>
          <Box
            display={"flex"}
            sx={{
              padding: "20px",
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: darkMode ? getColors.appBarBorderDark : getColors.appBarBorderLight,
              gap: 4,
              mb: 8,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", lg: "800px" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <FormControl fullWidth>
              <TextField
                fullWidth
                variant="filled"
                placeholder="Enter an email or phone number"
                {...register("senderInfo", {
                  required: "This field is required",
                  validate: (value) => {
                    if (!phoneRegex.test(value) && !isEmail(value)) {
                      return "Please enter a valid 10-digit US phone number or a valid email";
                    }
                    return true;
                  },
                })}
                error={!!errors.senderInfo}
                helperText={errors.senderInfo?.message}
              />
            </FormControl>

            {/* Regular Send */}
            <Button
              type="submit"
              variant="contained"
              endIcon={<SendIcon style={{ fontSize: 24, marginLeft: 8 }} />}
              disabled={loading}
              sx={{
                height: 64,
                fontSize: "18px",
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 6,
                py: "10px",
                borderRadius: "5px",
                whiteSpace: "nowrap",
              }}
            >
              Send
            </Button>

            <Tooltip title="Schedule functionality is only available for Email not SMS" disableHoverListener={isEmail(watch("senderInfo"))}>
              <span>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<ScheduleSendIcon sx={{ fontSize: 24 }} />}
                  disabled={loading || !isEmail(watch("senderInfo"))}
                  onClick={handleSubmit((data) => onSubmit(data, true))}
                  sx={{
                    height: 64,
                    fontSize: "18px",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    px: 7,
                    py: "10px",
                    borderRadius: "5px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Schedule
                </Button>
              </span>
            </Tooltip>
          </Box>
        </form>

        {error && <Typography color="error">{error}</Typography>}

        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: "20px", sm: "22px" },
            fontWeight: 500,
            color: darkMode ? getColors.drawerTextDark : getColors.thumbnailTextLight,
            mb: 2,
          }}
        >
          Available Videos
        </Typography>

        <Tabs
          value={videoLanguageTab}
          onChange={(_, value) => setVideoLanguageTab(value)}
          sx={{ mb: 2 }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="English" value="en" />
          <Tab label="Spanish" value="es" />
        </Tabs>

        <Box
          display="grid"
          gap={3}
          sx={{
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
          }}
        >
          {filteredVideos.length === 0 ? (
            <Typography>No videos found for this language.</Typography>
          ) : (
            filteredVideos.map((video) => {
              const selected = isSelected(video.id);
              return (
                <Box
                  key={video.id}
                  sx={{
                    cursor: "pointer",
                    width: "100%",
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderRadius: 1,
                    borderColor: selected
                      ? darkMode
                        ? getColors.thumbnailHoverBgLight
                        : getColors.thumbnailHoverBgDark
                      : darkMode
                      ? getColors.appBarBorderDark
                      : getColors.appBarBorderLight,
                    pl: 2,
                    py: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  onClick={() => toggleVideoSelect(video)}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "16px", sm: "18px" },
                      fontWeight: selected ? 600 : 500,
                      cursor: "pointer",
                      color: darkMode ? "#BEBEBE" : "#636363",
                    }}
                  >
                    {video.displayname}
                  </Typography>
                  <FormControlLabel
                    onClick={(e) => e.stopPropagation()}
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={() => toggleVideoSelect(video)}
                        sx={{ "&.Mui-checked": { color: "inherit" } }}
                      />
                    }
                    label=""
                  />
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </>
  );
};

export default SharePage;
